import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAtomValue } from 'jotai'
import { ChevronLeft, HelpCircle } from 'lucide-react'
import './Notices.scss'
import Sidebar from '../../components/Sidebar/Sidebar'
import MainURL from '../../MainURL'
import { drumtaleAdminSessionAtom, drumtaleAdminTokenAtom } from '../../AtomStore'
import { openPageSpecification } from '../shared/pageSpecification'
import {
  Notice,
  noticeEditorImplementationGuide,
  noticeEditorSpecification,
  noticeStatusOptions,
  noticesDataModelGuide,
  noticesStateDefinitions,
} from './noticesData'

const TOAST_DURATION_MS = 2400

function toLocalDateText(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function NoticeRegister() {
  const navigate = useNavigate()
  const { noticeId } = useParams()
  const isEdit = Boolean(noticeId)
  const isAdminSessionActive = useAtomValue(drumtaleAdminSessionAtom)
  const adminToken = useAtomValue(drumtaleAdminTokenAtom)
  const today = toLocalDateText(new Date())

  const [title, setTitle] = useState('')
  const [status, setStatus] = useState(noticeStatusOptions[0])
  const [start, setStart] = useState(today)
  const [end, setEnd] = useState(today)
  const [body, setBody] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isLoadingNotice, setIsLoadingNotice] = useState(isEdit)

  const [toastMessage, setToastMessage] = useState('')
  const [isToastVisible, setIsToastVisible] = useState(false)
  const toastTimerRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isAdminSessionActive) navigate('/')
  }, [isAdminSessionActive, navigate])

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current)
    }
  }, [])

  const showToast = (message: string) => {
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current)
    setToastMessage(message)
    setIsToastVisible(true)
    toastTimerRef.current = window.setTimeout(() => setIsToastVisible(false), TOAST_DURATION_MS)
  }

  useEffect(() => {
    if (!isEdit || !noticeId || !adminToken) return

    const loadNotice = async () => {
      setIsLoadingNotice(true)
      try {
        const response = await fetch(`${MainURL}/admin/notices/${noticeId}`, {
          headers: { Authorization: `Bearer ${adminToken}` },
        })
        const data = await response.json().catch(() => null)
        if (!response.ok) {
          showToast(data?.message || '공지를 불러오지 못했습니다.')
          navigate('/admin/notices')
          return
        }
        const notice = data?.notice
        if (!notice) {
          showToast('공지를 찾을 수 없습니다.')
          navigate('/admin/notices')
          return
        }
        setTitle(notice.title || '')
        setStatus(notice.status || noticeStatusOptions[0])
        setStart(notice.start || today)
        setEnd(notice.end || today)
        setBody(notice.body || '')
      } catch (error) {
        showToast('공지를 불러오지 못했습니다.')
        navigate('/admin/notices')
      } finally {
        setIsLoadingNotice(false)
      }
    }

    loadNotice()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, isEdit, noticeId])

  const handleOpenSpecification = () => {
    const opened = openPageSpecification(noticeEditorSpecification, noticesDataModelGuide, noticeEditorImplementationGuide, {
      stateDefinitions: noticesStateDefinitions,
    })
    if (!opened) showToast('화면 사양서 창을 열 수 없습니다. 팝업 차단을 해제해 주세요.')
  }

  const validate = (): string | null => {
    if (!title.trim()) return '공지 제목을 입력해 주세요.'
    if (!body.trim()) return '공지 내용을 입력해 주세요.'
    if (!start) return '시작일을 입력해 주세요.'
    if (!end) return '종료일을 입력해 주세요.'
    if (end < start) return '종료일은 시작일보다 빠를 수 없습니다.'
    return null
  }

  const handleSaveAndExit = async () => {
    const error = validate()
    if (error) {
      showToast(error)
      return
    }
    if (!adminToken) return

    setIsSaving(true)
    try {
      const payload = {
        title: title.trim(),
        status,
        start,
        end,
        body: body.trim(),
      }
      const response = await fetch(
        isEdit ? `${MainURL}/admin/notices/${noticeId}` : `${MainURL}/admin/notices`,
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: {
            Authorization: `Bearer ${adminToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
      )
      const data = await response.json().catch(() => null)
      if (!response.ok) {
        showToast(data?.message || (isEdit ? '공지 수정에 실패했습니다.' : '공지 저장에 실패했습니다.'))
        return
      }
      const savedNotice: Notice = data?.notice || { id: noticeId || '', ...payload }
      navigate('/admin/notices', {
        state: isEdit ? { updatedNotice: savedNotice } : { newNotice: savedNotice },
      })
    } catch (error) {
      showToast(isEdit ? '공지 수정에 실패했습니다. 네트워크 상태를 확인해 주세요.' : '공지 저장에 실패했습니다. 네트워크 상태를 확인해 주세요.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className='Notices'>
      <div className='app-shell'>
        <Sidebar />

        <main className='workspace'>
          <header className='topbar'>
            <div className='title-area'>
              <button className='back-button' type='button' onClick={() => navigate('/admin/notices')}>
                <ChevronLeft size={16} />
                뒤로가기
              </button>
              <h1>공지사항</h1>
            </div>
          </header>

          <section className='view active'>
            <div className='detail-stack'>
              <section className='detail-hero'>
                <p className='eyebrow'>공지사항</p>
                <h2>{isEdit ? '공지사항 수정' : '공지 등록'}</h2>
                <p>{isLoadingNotice ? '등록된 공지를 불러오는 중입니다.' : '공지에 필요한 내용만 입력합니다.'}</p>
              </section>

              <section className='panel'>
                <div className='modal-grid'>
                  <label className='modal-field'>
                    제목
                    <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder='공지 제목' disabled={isLoadingNotice} />
                  </label>
                  <label className='modal-field'>
                    상태
                    <select value={status} onChange={(event) => setStatus(event.target.value)} disabled={isLoadingNotice}>
                      {noticeStatusOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </label>
                  <label className='modal-field'>
                    시작일
                    <input type='date' value={start} onChange={(event) => setStart(event.target.value)} disabled={isLoadingNotice} />
                  </label>
                  <label className='modal-field'>
                    종료일
                    <input type='date' value={end} onChange={(event) => setEnd(event.target.value)} disabled={isLoadingNotice} />
                  </label>
                </div>
                <label className='modal-field'>
                  내용
                  <textarea value={body} onChange={(event) => setBody(event.target.value)} placeholder='공지 내용을 입력하세요.' disabled={isLoadingNotice} />
                </label>
              </section>

              <div className='detail-footer'>
                <button className='secondary-button' type='button' onClick={() => navigate('/admin/notices')}>취소</button>
                <button className='primary-button' type='button' onClick={handleSaveAndExit} disabled={isSaving || isLoadingNotice}>
                  {isSaving ? '저장 중...' : isEdit ? '수정 저장' : '공지 등록'}
                </button>
              </div>
            </div>
          </section>
        </main>
      </div>

      <button
        className='page-spec-button'
        type='button'
        aria-label='현재 화면 사양서 열기'
        title='현재 화면 사양서'
        onClick={handleOpenSpecification}
      >
        <HelpCircle size={22} />
      </button>

      <div className={`toast${isToastVisible ? ' show' : ''}`} role='status' aria-live='polite'>
        {toastMessage}
      </div>
    </div>
  )
}

export default NoticeRegister
